import type {
  CreditNoteStatus,
  DemandRunStatus,
  ExtractionStatus,
  InboundDiscrepancy,
  InboundProgressStage,
  InboundStatus,
  PoChangeStatus,
  PoLineStatus,
  PoStatus,
  ThreeWayResult,
} from "@nexxt/contracts/procurement";
import type { Tone } from "./tone";

/**
 * Tone of the states of a purchase order (spec v0.2 §5.3): `generated` waits for the approval of Operations, and
 * `in_validation` for its answer to the supplier; `approved` and `published` are steps on the way, `confirmed` is
 * the goal.
 */
export const poStatusTone: Record<PoStatus, Tone> = {
  generated: "warning",
  approved: "info",
  published: "info",
  in_validation: "warning",
  confirmed: "success",
  closed: "neutral",
  cancelled: "neutral",
};

export const poLineStatusTone: Record<PoLineStatus, Tone> = {
  open: "info",
  closed: "neutral",
  cancelled: "neutral",
};

/** A proposal of the supplier waits for Operations until it is decided or taken back. */
export const poChangeStatusTone: Record<PoChangeStatus, Tone> = {
  proposed: "warning",
  accepted: "success",
  rejected: "danger",
  withdrawn: "neutral",
};

/**
 * Tone of the states of an inbound (spec v0.2 §5.3, ADR-128): what waits on someone is a warning, a failed check is
 * a danger; from the passed check to the warehouse the steps are on the way, and the receipt is the goal.
 */
export const inboundStatusTone: Record<InboundStatus, Tone> = {
  draft: "neutral",
  documents_uploaded: "warning",
  pending_authorization: "warning",
  authorized: "warning",
  lines_captured: "warning",
  in_exception: "danger",
  three_way_passed: "info",
  ddt_generated: "info",
  in_transit: "info",
  partially_received: "warning",
  received: "success",
  closed: "neutral",
  cancelled: "neutral",
};

/** A forced check passed with a difference someone accepted (ADR-131): it stays visible. */
export const threeWayResultTone: Record<ThreeWayResult, Tone> = {
  passed: "success",
  failed: "danger",
  overridden: "warning",
};

/** A credit note waits for its file and then for Finance (ADR-140); a rejected one needs a new file. */
export const creditNoteStatusTone: Record<CreditNoteStatus, Tone> = {
  requested: "warning",
  received: "success",
  rejected: "danger",
};

/**
 * A run of the demand ends in the request that starts it (ADR-143): `converted` created POs to approve, `computed`
 * found nothing to order.
 */
export const demandRunStatusTone: Record<DemandRunStatus, Tone> = {
  draft: "neutral",
  computed: "neutral",
  converted: "success",
  cancelled: "neutral",
};

/**
 * Where the goods of a PO coming in stand (P8.10a, ADR-214): waiting for the supplier and received in part are what
 * Logistics follows; on the way is a step of the warehouse, received the goal.
 */
export const inboundProgressStageTone: Record<InboundProgressStage, Tone> = {
  awaiting_shipment: "warning",
  in_transit: "info",
  partially_received: "warning",
  received: "success",
};

/** An open discrepancy of an inbound waits for a person: a failed check is the one that stops the goods. */
export const inboundDiscrepancyTone: Record<InboundDiscrepancy, Tone> = {
  exception: "danger",
  to_resolve: "warning",
  to_settle: "warning",
};

/**
 * Tone of the reading of a packing list by the extraction tool (ADR-233): `to_review` waits for Logistics, a failure
 * leaves the typing to it, `captured` took the inbound to its check.
 */
export const extractionStatusTone: Record<ExtractionStatus, Tone> = {
  requested: "info",
  captured: "success",
  to_review: "warning",
  reviewed: "neutral",
  failed: "danger",
};
